import { useEffect, useRef, useState } from "react";
import { useSlideInMenu } from "../../components/slideInMenu/hooks/useSlideInMenu";
import { NavigationTrigger } from "../reader/types";
import { Button, Flex, Text, useBreakpoint } from "@chakra-ui/react";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { Chat } from "../../components/chat/Chat";
import { AblyWrapper } from "../../components/chat/components/AblyWrapper";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore";
import { EyeBlindIcon } from "../../components/icons/EyeBlindIcon";
import { EyeIcon } from "../../components/icons/EyeIcon";
import { ChatProvider } from "../../components/chat/hooks/useChat";
import { ChatHeader } from "../../components/chat/components/ChatHeader";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";

export function TrendsScreen() {
  const { openComicInfoMenu } = useSlideInMenu();
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const [showChat, setShowChat] = useState(true);
  const topBarHeight = useTopBarHeight();

  const initialSidebarState = useRef(rootComponentsStore.isSidebarOpen.get());
  const breakpoint = useBreakpoint({ ssr: false });
  const showSidebar = ["2xl", "3xl", "4xl"].includes(breakpoint);
  useEffect(() => {
    if (showSidebar) return;
    rootComponentsStore.isSidebarOpen.set(false);
    return () => {
      rootComponentsStore.isSidebarOpen.set(initialSidebarState.current);
    };
  }, []);

  useFeatureFlag("trends");

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== "https://map.globalcomixdev.com") {
        return;
      }
      const { comic_slug, comic_id } = event.data;
      openComicInfoMenu(comic_slug, {
        area: "trends",
        comicId: comic_id,
        trigger: NavigationTrigger.trendsComicCard,
      });
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <Flex
      position="relative"
      width="100%"
      height="100%"
      maxHeight={`calc(100vh - ${topBarHeight.get()}px)`}
      gap={isSmallBreakpoint ? "8px" : showChat ? "40px" : "0px"}
      flexDirection={isSmallBreakpoint ? "column" : "row"}
      p={isSmallBreakpoint ? "16px 16px 0px 16px" : "40px 40px 48px 0px"}
    >
      <Flex
        flex={isSmallBreakpoint ? undefined : "1"}
        flexDirection="column"
        gap="16px"
        px="0px"
        flexGrow="1"
      >
        {isSmallBreakpoint ? null : (
          <Flex alignItems="center" justifyContent="space-between">
            <Text
              as="h1"
              fontSize="32px"
              letterSpacing=".5px"
              fontWeight="700"
              fontFamily="Bricolage Grotesque"
              color="dune.dune"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              Pulse
            </Text>
            <Button
              size="small"
              variant="tertiary"
              p="6px 16px"
              height="26px"
              borderRadius="10px"
              gap="8px"
              onClick={() => setShowChat(!showChat)}
            >
              {showChat ? (
                <EyeBlindIcon boxSize="14px" color="dune.100" />
              ) : (
                <EyeIcon boxSize="14px" color="dune.100" />
              )}
              <Text
                fontSize="12px"
                color="dune.dune"
                letterSpacing=".75px"
                lineHeight="11px"
                fontWeight="500"
              >
                {showChat ? "Hide" : "Show"} chat
              </Text>
            </Button>
          </Flex>
        )}
        <iframe
          allow="geolocation"
          style={{
            borderRadius: "24px",
            minHeight: "0",
          }}
          width="100%"
          height="100%"
          src="https://map.globalcomixdev.com/public"
        />
      </Flex>
      <AblyWrapper channelId="trends" featureFlag="trends">
        <ChatProvider roomId="trends">
          <Flex
            width={isSmallBreakpoint ? "100%" : showChat ? "400px" : "0px"}
            opacity={isSmallBreakpoint || showChat ? 1 : 0}
            transition="width .2s linear, min-height .2s linear, opacity .2s linear"
            height="auto"
            overflow="hidden"
            position="relative"
            minHeight={
              isSmallBreakpoint ? (!showChat ? "0px" : "60vh") : "100%"
            }
          >
            <Chat
              compact={isSmallBreakpoint}
              smallHeader
              eventStartTime="0"
              flex={1}
              opacity={showChat ? 1 : 0}
              transition="opacity .2s linear"
              minWidth="400px"
              width="100%"
              height="100%"
              position="absolute"
              left="0"
              top="0"
              overflow="hidden"
              title="Live Chat"
              button={
                isSmallBreakpoint ? (
                  <Button
                    onClick={() => setShowChat(!showChat)}
                    size="small"
                    variant="tertiary"
                    p="6px 8px"
                    height="26px"
                    borderRadius="10px"
                    gap="8px"
                    mb="6px"
                    minWidth="0px"
                  >
                    {showChat ? (
                      <EyeBlindIcon boxSize="14px" color="dune.100" />
                    ) : (
                      <EyeIcon boxSize="14px" color="dune.100" />
                    )}
                  </Button>
                ) : undefined
              }
            />
            {isSmallBreakpoint && !showChat ? (
              <ChatHeader
                compact={true}
                title="Live Chat"
                button={
                  isSmallBreakpoint ? (
                    <Button
                      onClick={() => setShowChat(!showChat)}
                      size="small"
                      variant="tertiary"
                      p="6px 8px"
                      height="26px"
                      borderRadius="10px"
                      gap="8px"
                      mb="6px"
                      minWidth="0px"
                    >
                      {showChat ? (
                        <EyeBlindIcon boxSize="14px" color="dune.100" />
                      ) : (
                        <EyeIcon boxSize="14px" color="dune.100" />
                      )}
                    </Button>
                  ) : undefined
                }
              />
            ) : null}
          </Flex>
        </ChatProvider>
      </AblyWrapper>
    </Flex>
  );
}
